///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';

import {
    EngineAntennaDTO,
    EngineProductDTO,
    GtthKeyValueDTO,
    PdbChannelConfigDTO,
    PdbChannelFrequenciesEnum,
    ProductAccessoryDTO,
    ProductAccessoryTypeEnum,
    ProductDeviceTypeEnum,
    ProductMhTypeEnum,
    ProductRadioDTO,
    ProductTypeEnum,
    ProjectProductsDTO
} from '../../../generated/shared-types.model';
import { BomDeviceConfigModel } from '../../project-windows/bom_report/dto/bom-device-config.model';
import { ProductRadioService } from '../../../services/product/product-radio.service';
import { ProjectService } from '../../../services/project/project.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ProjectProductsService {
    public static readonly MPL_PRODUCT_PREFIX = 'MPL';
    public static readonly MH_T280_TU_ID = 15;

    private readonly DEFAULT_ACCESSORY_BASE_ID = -1;
    private readonly MH_TU_1_PRODUCT_REAL_SUFIX = 'T200-CNN';
    private readonly MH_TU_3_PRODUCT_REAL_SUFIX = 'T200-CCC';
    private readonly MH_TU_COMPACT_REAL_SUFIX = 'T201-CNN';

    private engineRadioList: Map<number, EngineProductDTO>;
    private engineAntennas: EngineAntennaDTO[];
    private productRadio: ProductRadioDTO[];
    private accessories: ProductAccessoryDTO[];
    private currencyCode: string;

    // radio id -> antenna id -> accessory type -> array of available values
    private bomAvailable: Map<number, Map<number, Map<ProductAccessoryTypeEnum, BomAccessoryEntity[]>>>;
    private bomIncluded: Map<number, Map<number, Map<ProductAccessoryTypeEnum, GtthKeyValueDTO>>>;
    private bomDeviceConfigMap: Map<number, Map<number, BomDeviceConfigModel>>;

    constructor(
        private readonly productRadioService: ProductRadioService,
        private readonly projectService: ProjectService
    ) {}

    public isMh(type: number): boolean {
        return this.engineRadioList.has(type) ? this.engineRadioList.get(type).productType === ProductTypeEnum.MH : false;
    }

    public isMhTgDevice(type: number): boolean {
        return this.engineRadioList.has(type) ? this.engineRadioList.get(type).productType === ProductTypeEnum.TG_MH : false;
    }

    public isMhTgBuDevice(type: number): boolean {
        return this.engineRadioList.has(type)
            ? this.engineRadioList.get(type).productType === ProductTypeEnum.TG_MH && this.engineRadioList.get(type).isBU
            : false;
    }

    public isDeviceHasSectors(type: number): boolean {
        if (this.engineRadioList.has(type) && this.engineRadioList.get(type).pdbConfig?.sectorConfig.length > 0) {
            const firstSectorConfig = this.engineRadioList.get(type).pdbConfig.sectorConfig[0];
            return firstSectorConfig && firstSectorConfig.maxBu + firstSectorConfig.maxTu > 1;
        }

        return false;
    }

    public isDeviceBU(type: number): boolean {
        return this.engineRadioList.has(type) && this.engineRadioList.get(type).isBU;
    }

    public isDeviceTU(type: number): boolean {
        return this.engineRadioList.has(type) && this.engineRadioList.get(type).isTU;
    }

    public isDevicePtP(type: number): boolean {
        return this.engineRadioList.has(type) && this.engineRadioList.get(type).isPtP;
    }

    getCurrencyCode() {
        if (!this.currencyCode) {
            if (this.productRadio && this.productRadio.length > 0) {
                this.currencyCode = this.productRadio[0].currency;
            } else {
                this.currencyCode = 'USD';
            }
        }
        return this.currencyCode;
    }

    public async loadProducts(projectId: number, force: boolean): Promise<void> {
        if (!force && this.productRadio && this.accessories) {
            return Promise.resolve();
        }

        this.currencyCode = undefined;
        this.accessories = [];
        this.engineRadioList = new Map();
        this.engineAntennas = [];

        const projectRadio: ProjectProductsDTO = await firstValueFrom(this.productRadioService.queryForProject(projectId));
        this.productRadio = projectRadio.products;
        this.engineAntennas = projectRadio.antennas;

        this.productRadio.forEach((radio: ProductRadioDTO) => {
            if (radio.availableAccessories) {
                radio.availableAccessories.forEach((accessory: ProductAccessoryDTO) => {
                    this.accessories.push(accessory);
                });
            }

            if (!this.engineRadioList.has(radio.baseEntityId)) {
                const engineRadio = {
                    id: radio.baseEntityId,
                    name: radio.baseEntityName
                } as EngineProductDTO;
                this.engineRadioList.set(radio.baseEntityId, engineRadio);
            }
        });

        const baseProducts = await firstValueFrom(this.projectService.getLinksProducts(projectId));

        baseProducts.forEach((product: EngineProductDTO) => {
            this.engineRadioList.set(product.id, product);
        });

        this.prepareDataForBom();
    }

    public getRadio(): ProductRadioDTO[] {
        return this.productRadio;
    }

    public getRadiosByBaseId(baseId: number): ProductRadioDTO[] {
        return this.productRadio.filter((radio: ProductRadioDTO) => radio.baseEntityId === baseId);
    }

    getRadioByName(name: string): ProductRadioDTO {
        return this.productRadio?.find((radio) => radio.name === name);
    }

    getRadioById(id: number): ProductRadioDTO {
        return this.productRadio.find((radio) => radio.id === id);
    }

    getAccessoryById(id: number): ProductAccessoryDTO {
        return this.accessories.find((accessory) => accessory.id === id);
    }

    getAntennaByBaseId(antennaId: number): EngineAntennaDTO {
        return this.engineAntennas.find((antenna) => antenna.id === antennaId);
    }

    getBaseRadioNameById(usedProd: number): string {
        return this.engineRadioList.get(usedProd).name;
    }

    getBaseRadioById(usedProduct: number): EngineProductDTO {
        return this.engineRadioList.get(usedProduct);
    }

    getBaseRadioFrequenciesById(usedProduct: number): PdbChannelFrequenciesEnum[] {
        let frequencies: PdbChannelFrequenciesEnum[] = [];
        const engineProduct = this.engineRadioList.get(usedProduct);
        if (engineProduct.pdbConfig.channelConfigs?.length) {
            engineProduct.pdbConfig.channelConfigs.forEach((channelConfig: PdbChannelConfigDTO) => {
                frequencies = [...frequencies, ...channelConfig.frequencies];
            });
            const frequenciesSet = new Set<number>(frequencies);
            frequencies = Array.from(frequenciesSet.values());
            frequencies.sort((a, b) => a - b);
        }
        return frequencies;
    }

    clearData() {
        this.engineRadioList.clear();
        this.engineAntennas = [];
        this.productRadio = [];
        this.accessories = [];
        this.currencyCode = '';
    }

    public getTu1portRadio(): ProductRadioDTO {
        return this.getRadioByNameContains(this.MH_TU_1_PRODUCT_REAL_SUFIX);
    }

    public getTu3portRadio(): ProductRadioDTO {
        return this.getRadioByNameContains(this.MH_TU_3_PRODUCT_REAL_SUFIX);
    }

    public getTuCompactRadio(): ProductRadioDTO {
        return this.getRadioByNameContains(this.MH_TU_COMPACT_REAL_SUFIX);
    }

    //    api for bom

    public getBomRadios(baseEntityId: number, isSectorBu: boolean, deviceType: ProductDeviceTypeEnum): GtthKeyValueDTO[] {
        const mhType = isSectorBu ? ProductMhTypeEnum.BU : ProductMhTypeEnum.TU;

        return this.productRadio
            .filter((radio: ProductRadioDTO) => {
                if (radio.baseEntityId !== baseEntityId) {
                    return false;
                }

                if (radio.mhType !== ProductMhTypeEnum.NONE) {
                    return radio.productType === ProductTypeEnum.TG_MH || radio.mhType === mhType;
                } else {
                    return deviceType === ProductDeviceTypeEnum.NONE || radio.deviceType === deviceType;
                }
            })
            .map((radio: ProductRadioDTO) => {
                return { key: radio.id, value: radio.name } as GtthKeyValueDTO;
            });
    }

    public getBomDeviceConfig(radioId: number, antennaId: number): BomDeviceConfigModel {
        if (this.bomDeviceConfigMap.has(radioId) && this.bomDeviceConfigMap.get(radioId).has(antennaId)) {
            return this.bomDeviceConfigMap.get(radioId).get(antennaId);
        } else {
            return new BomDeviceConfigModel({}, {});
        }
    }

    public getAvailableAntenna(radioId: number, antennaId: number): GtthKeyValueDTO[] {
        const availableArray = this.getAvailableAccessories(radioId, antennaId, ProductAccessoryTypeEnum.ANTENNA).map((value) =>
            value.getKeyValue()
        );

        const originAntenna = this.engineAntennas.find((antenna) => antenna.id === antennaId);

        this.engineAntennas.forEach((antenna) => {
            if (originAntenna.id !== antenna.id && originAntenna.engineId === antenna.engineId) {
                this.getAvailableAccessories(radioId, antenna.id, ProductAccessoryTypeEnum.ANTENNA)
                    .map((value) => value.getKeyValue())
                    .forEach((value) => {
                        if (!availableArray.find((addedValue) => addedValue.key === value.key)) {
                            availableArray.push(value);
                        }
                    });
            }
        });

        return availableArray;
    }

    private getRadioByNameContains(namePart: string): ProductRadioDTO {
        return this.productRadio.find((value) => value.name.includes(namePart));
    }

    private getBomConfig(radioId: number, antennaId: number): BomDeviceConfigModel {
        const config = new BomDeviceConfigModel({}, {});

        this.setBomConfigForType(
            config,
            this.getAvailableAntenna(radioId, antennaId),
            this.getIncludedAntenna(radioId, antennaId),
            ProductAccessoryTypeEnum.ANTENNA
        );

        this.setBomConfigForType(
            config,
            this.getAvailableMk(radioId, antennaId),
            this.getIncludedMk(radioId, antennaId),
            ProductAccessoryTypeEnum.MK
        );

        this.setBomConfigForType(
            config,
            this.getAvailablePoe(radioId),
            this.getIncludedPoe(radioId),
            ProductAccessoryTypeEnum.POWER_INJECTORS
        );

        this.setBomConfigForType(config, this.getAvailableSrg(radioId), this.getIncludedSrg(radioId), ProductAccessoryTypeEnum.SRG);

        this.setBomConfigForType(config, this.getAvailableSfp(radioId), this.getIncludedSfp(radioId), ProductAccessoryTypeEnum.SFP);

        this.setBomConfigForType(
            config,
            this.getAvailableCapacityLicense(radioId),
            this.getIncludedCapacityLicense(radioId),
            ProductAccessoryTypeEnum.SOFTWARE_UPGRADE,
            false
        );

        this.setBomConfigForType(config, this.getAvailablePse(radioId), undefined, ProductAccessoryTypeEnum.OPT_PSE, false);

        this.setBomConfigForType(config, this.getAvailableL2(radioId), undefined, ProductAccessoryTypeEnum.OPT_L2, false);

        this.setBomConfigForType(config, this.getAvailableExtendmm(radioId), undefined, ProductAccessoryTypeEnum.OPT_EXTENDMM, false);

        this.setBomConfigForType(config, this.getAvailableSync(radioId), undefined, ProductAccessoryTypeEnum.OPT_SYNC, false);

        this.setBomConfigForType(config, this.getAvailableAes(radioId), undefined, ProductAccessoryTypeEnum.OPT_AES, false);

        this.setBomConfigForType(
            config,
            this.getAvailableSectorLicense(radioId),
            undefined,
            ProductAccessoryTypeEnum.SECTOR_LICENSE,
            false
        );

        return config;
    }

    private setBomConfigForType(
        config: BomDeviceConfigModel,
        available: GtthKeyValueDTO[],
        included: GtthKeyValueDTO,
        type: ProductAccessoryTypeEnum,
        selectIncluded = true
    ) {
        config.availableList[type] = available;
        if (included) {
            config.availableList[type].push(included);
        }

        if (selectIncluded && included) {
            config.value[type] = included;
        } else if (config.availableList[type] && config.availableList[type].length > 0) {
            config.value[type] = config.availableList[type][0];
        } else {
            config.availableList[type].push({ key: -1, value: 'N/A' } as GtthKeyValueDTO);
            config.value[type] = config.availableList[type][0];
        }
    }

    private prepareDataForBom(): void {
        this.bomAvailable = new Map<number, Map<number, Map<ProductAccessoryTypeEnum, BomAccessoryEntity[]>>>();
        this.bomIncluded = new Map<number, Map<number, Map<ProductAccessoryTypeEnum, GtthKeyValueDTO>>>();
        this.bomDeviceConfigMap = new Map<number, Map<number, BomDeviceConfigModel>>();

        this.productRadio.forEach((radio: ProductRadioDTO) => {
            this.bomAvailable.set(radio.id, new Map<number, Map<ProductAccessoryTypeEnum, BomAccessoryEntity[]>>());
            this.bomIncluded.set(radio.id, new Map<number, Map<ProductAccessoryTypeEnum, GtthKeyValueDTO>>());

            this.setRadioAvailableValues(radio);
            this.setRadioIncludedValues(radio);

            const configMap = new Map<number, BomDeviceConfigModel>();
            this.bomDeviceConfigMap.set(radio.id, configMap);

            this.engineAntennas.forEach((antenna: EngineAntennaDTO) => {
                configMap.set(antenna.id, this.getBomConfig(radio.id, antenna.id));
            });
        });
    }

    private setRadioAvailableValues(radio: ProductRadioDTO): void {
        const radioAvailableMap = this.bomAvailable.get(radio.id);

        radio.availableAccessories.forEach((accessory: ProductAccessoryDTO) => {
            let accessoryType = accessory.accessoryType;
            let baseId = accessory.baseEntityIds;
            if (accessoryType === ProductAccessoryTypeEnum.COMPOSITE) {
                accessoryType = accessory.relatedAccessories[0].accessoryType;
                baseId = accessory.relatedAccessories[0].baseEntityIds;
            }

            if (!baseId || baseId.length === 0) {
                baseId = [this.DEFAULT_ACCESSORY_BASE_ID];
            }

            baseId.forEach((baseEntityId) => {
                if (!radioAvailableMap.has(baseEntityId)) {
                    radioAvailableMap.set(baseEntityId, new Map<ProductAccessoryTypeEnum, BomAccessoryEntity[]>());
                }

                if (!radioAvailableMap.get(baseEntityId).has(accessoryType)) {
                    radioAvailableMap.get(baseEntityId).set(accessoryType, []);
                }

                radioAvailableMap
                    .get(baseEntityId)
                    .get(accessoryType)
                    .push(new BomAccessoryEntity(accessory.id, accessory.name, accessory.price));
            });
        });
    }

    private setRadioIncludedValues(radio: ProductRadioDTO): void {
        const radioIncludedMap = this.bomIncluded.get(radio.id);

        radioIncludedMap.set(this.DEFAULT_ACCESSORY_BASE_ID, new Map<ProductAccessoryTypeEnum, GtthKeyValueDTO>());
        radioIncludedMap.get(this.DEFAULT_ACCESSORY_BASE_ID).set(ProductAccessoryTypeEnum.SOFTWARE_UPGRADE, {
            key: -1,
            value: radio.capacityLicense + ' Mbps (Included) '
        } as GtthKeyValueDTO);

        radio.includedAccessories.forEach((accessory: ProductAccessoryDTO) => {
            let accessoryType = accessory.accessoryType;
            let baseId = accessory.baseEntityIds;
            if (accessoryType === ProductAccessoryTypeEnum.COMPOSITE) {
                accessoryType = accessory.relatedAccessories[0].accessoryType;
                baseId = accessory.relatedAccessories[0].baseEntityIds;
            }

            if (!baseId || baseId.length === 0) {
                baseId = [this.DEFAULT_ACCESSORY_BASE_ID];
            }

            baseId.forEach((baseEntityId) => {
                if (!radioIncludedMap.has(baseEntityId)) {
                    radioIncludedMap.set(baseEntityId, new Map<ProductAccessoryTypeEnum, GtthKeyValueDTO>());
                }

                let accessoryEntity: GtthKeyValueDTO;
                if (accessoryType === ProductAccessoryTypeEnum.ANTENNA) {
                    accessoryEntity = { key: accessory.id, value: accessory.name } as GtthKeyValueDTO;
                } else if (accessory.name === 'Included') {
                    accessoryEntity = { key: accessory.id, value: accessory.name } as GtthKeyValueDTO;
                } else {
                    accessoryEntity = {
                        key: accessory.id,
                        value: accessory.name + ' (Included) '
                    } as GtthKeyValueDTO;
                }

                radioIncludedMap.get(baseEntityId).set(accessoryType, accessoryEntity);
            });
        });
    }

    private getIncludedAntenna(radioId: number, antennaId: number): GtthKeyValueDTO {
        let included = this.getIncludedAccessory(radioId, antennaId, ProductAccessoryTypeEnum.ANTENNA);
        if (included === undefined) {
            included = this.getIncludedAccessory(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.ANTENNA);
            if (included === undefined) {
                return undefined;
            }
        }

        const mhType = this.getRadioById(radioId).mhType;

        if (included.value === 'Included') {
            if (mhType !== ProductMhTypeEnum.NONE) {
                return { key: -1, value: '( Integrated beam-steering )' } as GtthKeyValueDTO;
            } else {
                const antennaDTO = this.getAntennaByBaseId(antennaId);
                if (antennaDTO) {
                    return { key: -1, value: `( Integrated ${antennaDTO.name} )` } as GtthKeyValueDTO;
                } else {
                    return { key: -1, value: '( Integrated )' } as GtthKeyValueDTO;
                }
            }
        } else {
            return { key: included.key, value: included.value + ' ( Included ) ' } as GtthKeyValueDTO;
        }
    }

    private getIncludedMk(radioId: number, antennaId: number): GtthKeyValueDTO {
        let included = this.getIncludedAccessory(radioId, antennaId, ProductAccessoryTypeEnum.MK);
        included ??= this.getIncludedAccessory(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.MK);

        return included;
    }

    private getIncludedPoe(radioId: number): GtthKeyValueDTO {
        return this.getIncludedAccessory(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.POWER_INJECTORS);
    }

    private getIncludedSrg(radioId: number): GtthKeyValueDTO {
        return this.getIncludedAccessory(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.SRG);
    }

    private getIncludedSfp(radioId: number): GtthKeyValueDTO {
        return this.getIncludedAccessory(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.SFP);
    }

    private getIncludedCapacityLicense(radioId: number): GtthKeyValueDTO {
        return this.getIncludedAccessory(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.SOFTWARE_UPGRADE);
    }

    private getAvailableMk(radioId: number, antennaId: number): GtthKeyValueDTO[] {
        const availableArray = this.getAvailableAccessories(radioId, antennaId, ProductAccessoryTypeEnum.MK).map((value) =>
            value.getKeyValue()
        );

        const originAntenna = this.engineAntennas.find((antenna) => antenna.id === antennaId);

        this.engineAntennas.forEach((antenna) => {
            if (originAntenna.id !== antenna.id && originAntenna.engineId === antenna.engineId) {
                this.getAvailableAccessories(radioId, antenna.id, ProductAccessoryTypeEnum.MK)
                    .map((value) => value.getKeyValue())
                    .forEach((value) => {
                        if (!availableArray.find((addedValue) => addedValue.key === value.key)) {
                            availableArray.push(value);
                        }
                    });
            }
        });

        return availableArray;
    }

    private getAvailablePoe(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.POWER_INJECTORS).map(
            (value) => value.getKeyValue()
        );
    }

    private getAvailableSrg(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.SRG).map((value) =>
            value.getKeyValue()
        );
    }

    private getAvailableSfp(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.SFP).map((value) =>
            value.getKeyValue()
        );
    }

    private getAvailableCapacityLicense(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.SOFTWARE_UPGRADE).map(
            (value) => value.getKeyValue()
        );
    }

    private getAvailableSectorLicense(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.SECTOR_LICENSE).map((value) =>
            value.getKeyValue()
        );
    }

    private getAvailablePse(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.OPT_PSE)
            .sort((a, b) => a.price - b.price)
            .map((value) => value.getKeyValue());
    }

    private getAvailableL2(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.OPT_L2)
            .sort((a, b) => a.price - b.price)
            .map((value) => value.getKeyValue());
    }

    private getAvailableExtendmm(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.OPT_EXTENDMM)
            .sort((a, b) => a.price - b.price)
            .map((value) => value.getKeyValue());
    }

    private getAvailableSync(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.OPT_SYNC)
            .sort((a, b) => a.price - b.price)
            .map((value) => value.getKeyValue());
    }

    private getAvailableAes(radioId: number): GtthKeyValueDTO[] {
        return this.getAvailableAccessories(radioId, this.DEFAULT_ACCESSORY_BASE_ID, ProductAccessoryTypeEnum.OPT_AES)
            .sort((a, b) => a.price - b.price)
            .map((value) => value.getKeyValue());
    }

    private getAvailableAccessories(radioId: number, antennaId: number, accessoryType: ProductAccessoryTypeEnum): BomAccessoryEntity[] {
        if (
            this.bomAvailable.has(radioId) &&
            this.bomAvailable.get(radioId).has(antennaId) &&
            this.bomAvailable.get(radioId).get(antennaId).has(accessoryType)
        ) {
            return this.bomAvailable.get(radioId).get(antennaId).get(accessoryType);
        } else {
            return [];
        }
    }

    private getIncludedAccessory(radioId: number, antennaId: number, accessoryType: ProductAccessoryTypeEnum): GtthKeyValueDTO {
        if (
            this.bomIncluded.has(radioId) &&
            this.bomIncluded.get(radioId).has(antennaId) &&
            this.bomIncluded.get(radioId).get(antennaId).has(accessoryType)
        ) {
            return this.bomIncluded.get(radioId).get(antennaId).get(accessoryType);
        } else {
            return undefined;
        }
    }
}

class BomAccessoryEntity {
    constructor(
        public id: number,
        public name: string,
        public price: number
    ) {}

    getKeyValue(): GtthKeyValueDTO {
        return { key: this.id, value: this.name } as GtthKeyValueDTO;
    }
}
